import React from "react";
import { Outlet } from "react-router-dom";
import styled from "styled-components";
import { IconButton } from "../components";
import { FormCheck } from "../components/forms";
import { PageHeaderTop } from "../components/layout/PageHeader";
import { Spinner } from "../components/loaders";
import { Main, PageHeader, PageSubHeader, ThemedIcon, ThemedParagraph } from "../components/utilities";
import Wp3Dropdown from "../components/utilities/Dropdown";
import AssetsUnderAdviceFiltersModal from "../features/assetsunderadvicegrid/components/AssetsUnderAdviceFilterModal";
import ValuationBuildPicker from "../features/assetsunderadvicegrid/components/ValuationBuildPicker";
import { AssetsUnderAdviceProvider, AssetViewTypes, useAssetsUnderAdvice } from "../features/assetsunderadvicegrid/hooks/AssetsUnderAdviceContext";
import CreateClientSetModal from "../features/createclientsetmodal";
import { AssetsUnderAdviceDefaultFilters, FilterProvider } from "../hooks/FilterContext";
import withProviders from "../hooks/withProviders";
import { AssetAnalysisRouteTabs } from "../routes/administration.analysis.assets.routes";

const AssetsUnderAdvicePageHeader = styled(PageHeader)`
    padding: 1rem 1rem 0 1rem;
`;

const PageTitle = styled(PageHeaderTop)`
    font-size: 16px;
`;

const PageHeaderBottom = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const TotalWrapper = styled.div`
    display: flex;
    font-size: 16px;
`;

const AssetsUnderAdviceSubHeader = ({ setShowCreateSetModal }) => {
    const {
        viewType,
        buildId,
        group,
        flipGroup,
        clearFilter,
        setShowFilterModal,
        isFiltersApplied,
        canExport,
        exportExcel,
    } = useAssetsUnderAdvice();

    return <PageSubHeader className="d-flex justify-content-between px-4">
        <span className="d-flex">
            {isFiltersApplied && (
                <React.Fragment>
                    <ThemedIcon className="my-auto" icon="fa-triangle-exclamation" size="2x" variant="warning" />
                    <ThemedParagraph className="ms-2 text-muted my-auto">
                        Warning - Filters are currently being applied to your results.
                        <br />
                        <button className="anchor" onClick={clearFilter}>Click here</button> to clear your filters.
                    </ThemedParagraph>
                </React.Fragment>
            )}
            <ValuationBuildPicker />
            {viewType === AssetViewTypes.FundsSummary && <FormCheck
                groupClassName="mt-auto mb-2 mx-2"
                className="ms-0"
                label="Group?"
                labelFirst
                vertical
                isChecked={group}
                onChange={flipGroup}
                disableAnimations
            />}
        </span>
        <IconButton
            className="mt-auto"
            icon={['fas', 'filter']}
            onClick={() => setShowFilterModal(true)}
            menu={[
                <Wp3Dropdown.Menu>
                    <Wp3Dropdown.MenuItem
                        icon={(['fas', 'plus'])}
                        text="Create new Client Set"
                        onClick={() => setShowCreateSetModal(true)}
                        disabled={buildId == null}
                    />
                    {canExport && <Wp3Dropdown.MenuItem
                        icon={"file-export"}
                        text="Export"
                        onClick={() => exportExcel(true)}
                        disabled={buildId == null}
                    />}
                </Wp3Dropdown.Menu>
            ]}
        >
            Filters
        </IconButton>
    </PageSubHeader>
}

const AssetsUnderAdviceLayout = () => {
    const {
        setViewType,
        buildId,
        showFilterModal,
        setShowFilterModal,
        totalValue,
        totalCount,
        isError,
        isClearing,
        createClientSet
    } = useAssetsUnderAdvice();

    const [showCreateSetModal, setShowCreateSetModal] = React.useState(false);

    return <>
        <AssetsUnderAdviceFiltersModal
            handleClose={() => setShowFilterModal(false)}
            show={showFilterModal}
        />
        <CreateClientSetModal
            setCount={totalCount}
            createClientSet={createClientSet}
            onHide={() => setShowCreateSetModal(false)}
            show={showCreateSetModal}
        />
        <AssetsUnderAdvicePageHeader>
            <span className="d-flex justify-content-between">
                <PageTitle>
                    Assets Under Advice
                </PageTitle>
            </span>
            <PageHeaderBottom>
                <AssetAnalysisRouteTabs onTabChanged={(tab) => setViewType(tab?.viewType)} />
                {buildId != null && <TotalWrapper>
                    Total Value:&nbsp;
                    {isClearing
                        ? <Spinner className="ms-1" />
                        : !isError && <span>
                            {totalValue?.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                        </span>}
                </TotalWrapper>}
            </PageHeaderBottom>
        </AssetsUnderAdvicePageHeader>
        <Main className="d-flex flex-column">
            <AssetsUnderAdviceSubHeader setShowCreateSetModal={setShowCreateSetModal} />
            <Outlet />
        </Main>
    </>
}

export default withProviders([FilterProvider, { filterStore: "assetsUnderAdvice", defaultFilters: AssetsUnderAdviceDefaultFilters }], AssetsUnderAdviceProvider)(AssetsUnderAdviceLayout);